/**
 * Product policy for the wallet button.
 *
 * Stripe remains the source of truth for wallet capability. The user agent is
 * consulted only when the client explicitly chooses the deliberately narrow
 * iPhone Safari Apple Pay policy. Google Pay remains a simple on/off decision.
 */

export const APPLE_PAY_SCOPE = Object.freeze({
  OFF: 'off',
  IPHONE_SAFARI: 'iphone_safari',
  STRIPE_SUPPORTED: 'stripe_supported',
});

const VALID_APPLE_SCOPES = new Set(Object.values(APPLE_PAY_SCOPE));

const normalizeScope = (value, validScopes, fallback) => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  // A typo in a production build must not widen a wallet's reach.
  return validScopes.has(value) ? value : 'off';
};

export const normalizeApplePayScope = value =>
  normalizeScope(value, VALID_APPLE_SCOPES, APPLE_PAY_SCOPE.STRIPE_SUPPORTED);

/**
 * This is intentionally a policy predicate, not a wallet-capability check.
 * Stripe's canMakePayment() result must still report the wallet afterwards.
 */
export const isIPhoneSafari = userAgent => {
  const ua = String(userAgent || '');
  const isIPhone = /iPhone/i.test(ua);
  // Safari's Version token avoids treating an embedded iOS webview that happens
  // to contain `Safari` in its UA as the standalone Safari browser.
  const isSafari = /Version\/\d[\d.]*.*Safari\//i.test(ua);
  const isOtherIOSBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//i.test(ua);

  return isIPhone && isSafari && !isOtherIOSBrowser;
};

/**
 * Selects the one wallet that may be shown. Unknown Stripe capabilities are
 * treated as unavailable; the UI must never render a generic payment button.
 */
export const selectWalletType = ({
  capabilityResult,
  applePayScope = APPLE_PAY_SCOPE.STRIPE_SUPPORTED,
  googlePayEnabled = false,
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '',
}) => {
  const result = capabilityResult || {};
  const appleScope = normalizeApplePayScope(applePayScope);

  const applePayAllowedByPolicy =
    appleScope === APPLE_PAY_SCOPE.STRIPE_SUPPORTED ||
    (appleScope === APPLE_PAY_SCOPE.IPHONE_SAFARI && isIPhoneSafari(userAgent));

  if (result.applePay && applePayAllowedByPolicy) {
    return 'applePay';
  }

  if (result.googlePay && googlePayEnabled) {
    return 'googlePay';
  }

  return null;
};
